import { MUIDataTableOptions } from 'mui-datatables';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import { AddEntityToolbarButton } from '../../common/table/AddEntityToolbarButton';
import {
  SupportedLanguage,
  useDeleteLinkMutation,
  LinkDataFragment,
} from '../../../graphql/models';
import { QUERY_LINKS } from '../../../graphql/queries/link/links';
import { getTranslatedTagNameOrDefault } from '../../../model/Tag';
import { QUERY_LINKS_TABLE } from '../../../graphql/queries/link/links-table';
import { GenericTable } from '../../common/table/GenericTable';
import React from 'react';
import DeleteConfirmationDialog from '../../common/table/DeleteConfirmationDialog';
import { RowsToBeDeleted } from '../../common/table/types';
import {
  ResourcesTableRow,
  ResourcesTableRowWithTags,
  useResourcesTableColumns,
} from '../../common/table/resources/resources-table-columns';
import { NavigationUtil } from '../../../common/navigation/NavigationUtil';
import { ResourcePath } from '../../../common/navigation/path-types';
import { useRoutes } from '../../../common/routing/routes-hook';
import { languageCompare } from '../../../common/supported-languages';

interface Props {
  linkResources: LinkDataFragment[];
}

export const LinksTable = ({ linkResources }: Props): JSX.Element => {
  const { i18n, t } = useTranslation();
  const { routes } = useRoutes();
  const history = useHistory();

  const [rowsToBeDeleted, setRowsToBeDeleted] = React.useState<
    RowsToBeDeleted | undefined
  >();

  const handleDeleteConfirmation = async () => {
    if (rowsToBeDeleted) {
      await deleteRows();
    }
    setRowsToBeDeleted(undefined);
  };

  const handleDeleteCancellation = () => {
    setRowsToBeDeleted(undefined);
  };

  const onAddNewClick = (): void => history.push(routes.linksNew.path);

  const AddButton = (): JSX.Element => (
    <AddEntityToolbarButton
      tooltipText={t('phrases.addNewLink')}
      onClick={onAddNewClick}
    />
  );

  const resourcesTableDataWithTags: ResourcesTableRowWithTags[] =
    linkResources.map(({ id, content, originalLanguage, tags, authors }) => {
      const title =
        content.find(({ language }) => languageCompare(language, i18n))
          ?.title ??
        content.find((value) => value.language === originalLanguage)?.title ??
        '-';

      const formattedAuthors = authors.map((author) => author.name);
      const languages = content
        .filter(({ title: translatedTitle }) => translatedTitle.length > 0)
        .map((translation) => translation.language)
        .sort();

      const publicLink = NavigationUtil.publicResourceLinkFrom(
        ResourcePath.link,
        id,
        i18n.language as SupportedLanguage,
        languages
      );

      return {
        id,
        title,
        tags: tags || [],
        authors: formattedAuthors,
        languages,
        publicLink,
      };
    });

  const tableData: ResourcesTableRow[] = resourcesTableDataWithTags.map(
    ({ tags, ...restData }) => {
      const tagsRender = tags.map((tag) => {
        return getTranslatedTagNameOrDefault(
          tag,
          i18n.language as SupportedLanguage
        );
      });

      return {
        ...restData,
        tags: tagsRender,
      };
    }
  );

  const columns = useResourcesTableColumns(resourcesTableDataWithTags);

  const [deleteMutation] = useDeleteLinkMutation();

  const deleteRows = async () => {
    for (const row of rowsToBeDeleted?.data ?? []) {
      const id = linkResources[row.dataIndex].id;
      await deleteMutation({
        variables: { id },
        refetchQueries: [{ query: QUERY_LINKS }, { query: QUERY_LINKS_TABLE }],
      });
    }
  };

  const options: MUIDataTableOptions = {
    onRowsDelete: (rowsDeleted) => {
      setRowsToBeDeleted(rowsDeleted);
    },
    onRowClick(_: string[], rowMeta: { dataIndex: number; rowIndex: number }) {
      history.push(
        routes.linksEdit.pathWithArguments({
          id: String(linkResources[rowMeta.dataIndex].id),
        })
      );
    },
    customToolbar: AddButton,
    sortOrder: {
      name: 'id',
      direction: 'asc',
    },
  };

  return (
    <>
      <DeleteConfirmationDialog
        handleDeleteConfirmation={handleDeleteConfirmation}
        handleDeleteCancellation={handleDeleteCancellation}
        open={rowsToBeDeleted !== undefined}
      />
      <GenericTable
        title={t('navigation.links')}
        data={tableData}
        columns={columns}
        options={options}
      />
    </>
  );
};
